import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { Endpoints } from '@/api';
import { useFetch } from '@/utils/fetch';

export type ConditionFormFromGetMany =
  Endpoints['GET /condition-forms']['response']['conditionForms'][number];
export type ConditionFormFromGet = Endpoints['GET /condition-forms/:id']['response'];

export const useConditionForms = (
  params?: Endpoints['GET /condition-forms']['query'],
  options?: { enabled?: boolean }
) => {
  const fetch = useFetch<Endpoints['GET /condition-forms']>();

  return useQuery({
    queryKey: ['condition-forms', 'organization', params],
    queryFn: () => fetch('/condition-forms', params),
    enabled: options?.enabled,
  });
};

export const useConditionForm = (id: string | undefined, options?: { enabled?: boolean }) => {
  const fetch = useFetch<Endpoints['GET /condition-forms/:id']>();

  return useQuery({
    queryKey: ['condition-forms', id],
    queryFn: () => fetch(`/condition-forms/${id}`),
    enabled: (options?.enabled ?? true) && !!id,
  });
};

export const useUpdateConditionForm = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /condition-forms/:id']>();

  return useMutation({
    mutationFn: ({
      conditionFormId,
      ...body
    }: { conditionFormId: string } & Endpoints['PATCH /condition-forms/:id']['body']) =>
      fetch(`/condition-forms/${conditionFormId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', variables.conditionFormId] });
    },
  });
};

export const useCreateConditionFormQuestion = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /condition-forms/:id/questions']>();

  return useMutation({
    mutationFn: ({
      conditionFormId,
      ...body
    }: { conditionFormId: string } & Endpoints['POST /condition-forms/:id/questions']['body']) =>
      fetch(`/condition-forms/${conditionFormId}/questions`, undefined, {
        method: 'POST',
        body,
      }),
    onSuccess: (_, { conditionFormId }) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', conditionFormId] });
    },
  });
};

export const useCreateConditionFormAnswer = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['POST /condition-forms/:id/answers']>();

  return useMutation({
    mutationFn: ({
      conditionFormId,
      ...body
    }: { conditionFormId: string } & Endpoints['POST /condition-forms/:id/answers']['body']) =>
      fetch(`/condition-forms/${conditionFormId}/answers`, undefined, {
        method: 'POST',
        body,
      }),
    onSuccess: (_, { conditionFormId }) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', conditionFormId] });
    },
  });
};

export const useDeleteConditionFormAnswer = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /condition-forms/:id/answers/:answerId']>();

  return useMutation({
    mutationFn: ({ conditionFormId, answerId }: { conditionFormId: string; answerId: string }) =>
      fetch(`/condition-forms/${conditionFormId}/answers/${answerId}`, undefined, {
        method: 'DELETE',
      }),
    onSuccess: (_, { conditionFormId }) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', conditionFormId] });
    },
  });
};

export const useUpdateConditionFormAnswer = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /condition-forms/:id/answers/:answerId']>();

  return useMutation({
    mutationFn: ({
      conditionFormId,
      answerId,
      ...body
    }: {
      conditionFormId: string;
      answerId: string;
    } & Endpoints['PATCH /condition-forms/:id/answers/:answerId']['body']) =>
      fetch(`/condition-forms/${conditionFormId}/answers/${answerId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, { conditionFormId }) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', conditionFormId] });
    },
  });
};

export const useUpdateConditionFormQuestion = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /condition-forms/:id/questions/:questionId']>();

  return useMutation({
    mutationFn: ({
      conditionFormId,
      questionId,
      ...body
    }: {
      conditionFormId: string;
      questionId: string;
    } & Endpoints['PATCH /condition-forms/:id/questions/:questionId']['body']) =>
      fetch(`/condition-forms/${conditionFormId}/questions/${questionId}`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, { conditionFormId }) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', conditionFormId] });
    },
  });
};

export const useReorderConditionFormQuestions = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['PATCH /condition-forms/:id/questions/reorder']>();

  return useMutation({
    mutationFn: ({
      conditionFormId,
      ...body
    }: {
      conditionFormId: string;
    } & Endpoints['PATCH /condition-forms/:id/questions/reorder']['body']) =>
      fetch(`/condition-forms/${conditionFormId}/questions/reorder`, undefined, {
        method: 'PATCH',
        body,
      }),
    onSuccess: (_, { conditionFormId }) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', conditionFormId] });
    },
  });
};

export const useDeleteConditionFormQuestion = () => {
  const queryClient = useQueryClient();
  const fetch = useFetch<Endpoints['DELETE /condition-forms/:id/questions/:questionId']>();

  return useMutation({
    mutationFn: ({
      conditionFormId,
      questionId,
    }: {
      conditionFormId: string;
      questionId: string;
    }) =>
      fetch(`/condition-forms/${conditionFormId}/questions/${questionId}`, undefined, {
        method: 'DELETE',
      }),
    onSuccess: (_, { conditionFormId }) => {
      queryClient.invalidateQueries({ queryKey: ['condition-forms'] });
      queryClient.invalidateQueries({ queryKey: ['condition-forms', conditionFormId] });
    },
  });
};
